import React, { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useGetInfoQuery } from '../../../services/auth';
import { useGetCustomerByIdQuery } from '../../../services/customer/customer-service';
import { useGetDashboardMonthlyDataQuery } from '../../../services/dashboard-monthly';
import Skeletons from '../../Skeletons';
import {
  ChartContainer,
  ChartWrapper,
  MonthYearSelector,
  PeriodButton,
  PeriodSelector,
  SelectorContainer,
  TotalCard,
  TotalLabel,
  TotalsContainer,
  TotalValue,
} from './styles';

type PeriodType = 'monthly' | 'yearly';

interface ChartData {
  period: string;
  enrichment: number;
  leads: number;
  total: number;
}

interface DashboardMonthlyItem {
  customerId: string;
  leads: number;
  enrichment: number;
  contacts: number;
  connections: number;
  planValue: number;
  customerType: string;
  planDescription: string;
  maxQuantityEnrichment: number;
  maxQuantityLeads: number;
  segmentations: number;
  month: number;
  year: number;
}

const renderCustomizedLabel = (props: any) => {
  const { x = 0, y = 0, width = 0, value = '' } = props;
  if (value === 0) return null;
  return (
    <text
      x={x + width / 2}
      y={y - 6}
      fill="#333"
      textAnchor="middle"
      fontSize="12"
      fontWeight="500"
    >
      {value.toLocaleString('pt-BR')}
    </text>
  );
};

const monthNames = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

const monthMap: Record<string, number> = {
  janeiro: 1,
  fevereiro: 2,
  março: 3,
  abril: 4,
  maio: 5,
  junho: 6,
  julho: 7,
  agosto: 8,
  setembro: 9,
  outubro: 10,
  novembro: 11,
  dezembro: 12,
};

const DailyConsumptionChart: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('monthly');
  const [selectedMonth, setSelectedMonth] = useState('julho');
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);

  // Hooks para obter dados do usuário e dashboard
  const { data: info } = useGetInfoQuery();
  const { data: customer } = useGetCustomerByIdQuery(info?.customer || '', {
    skip: !info?.customer,
  });
  const { data: dashboardData, isLoading } = useGetDashboardMonthlyDataQuery(
    {},
  );

  // Processar dados para visualização mensal (dias do mês selecionado)
  const monthlyChartData = useMemo(() => {
    if (!dashboardData || !customer) return [];

    const selectedMonthIndex = monthMap[selectedMonth.toLowerCase()];
    const filteredData = (dashboardData as DashboardMonthlyItem[]).filter(
      item =>
        item.month === selectedMonthIndex &&
        item.year === selectedYear &&
        item.customerType === customer.customerType,
    );

    // Agrupar dados por dia (simulando dias baseado nos dados disponíveis)
    const dailyData: ChartData[] = [];
    const daysInMonth = new Date(selectedYear, selectedMonthIndex, 0).getDate();

    // Distribuir os dados ao longo dos dias do mês
    for (let day = 1; day <= Math.min(daysInMonth, 31); day += 1) {
      const dayData = filteredData[day - 1] || { leads: 0, enrichment: 0 };
      dailyData.push({
        period: day.toString().padStart(2, '0'),
        enrichment: dayData.enrichment || 0,
        leads: dayData.leads || 0,
        total: (dayData.enrichment || 0) + (dayData.leads || 0),
      });
    }

    return dailyData;
  }, [dashboardData, customer, selectedMonth, selectedYear]);

  // Processar dados para visualização anual (meses do ano selecionado)
  const yearlyChartData = useMemo(() => {
    if (!dashboardData || !customer) return [];

    const filteredData = (dashboardData as DashboardMonthlyItem[]).filter(
      item =>
        item.year === selectedYear &&
        item.customerType === customer.customerType,
    );

    // Agrupar dados por mês
    const monthlyData: ChartData[] = [];
    for (let month = 1; month <= 12; month += 1) {
      const monthData = filteredData.filter(item => item.month === month);
      const totalEnrichment = monthData.reduce(
        (sum, item) => sum + item.enrichment,
        0,
      );
      const totalLeads = monthData.reduce((sum, item) => sum + item.leads, 0);

      monthlyData.push({
        period: monthNames[month - 1],
        enrichment: totalEnrichment,
        leads: totalLeads,
        total: totalEnrichment + totalLeads,
      });
    }

    return monthlyData;
  }, [dashboardData, customer, selectedYear]);

  // Calcular totais
  const totals = useMemo(() => {
    if (!dashboardData || !customer) return { enrichment: 0, leads: 0 };

    const filteredData = (dashboardData as DashboardMonthlyItem[]).filter(
      item => {
        if (selectedPeriod === 'monthly') {
          const selectedMonthIndex = monthMap[selectedMonth.toLowerCase()];
          return (
            item.month === selectedMonthIndex &&
            item.year === selectedYear &&
            item.customerType === customer.customerType
          );
        }
        return (
          item.year === selectedYear &&
          item.customerType === customer.customerType
        );
      },
    );

    return {
      enrichment: filteredData.reduce((sum, item) => sum + item.enrichment, 0),
      leads: filteredData.reduce((sum, item) => sum + item.leads, 0),
    };
  }, [dashboardData, customer, selectedPeriod, selectedMonth, selectedYear]);

  const chartData =
    selectedPeriod === 'monthly' ? monthlyChartData : yearlyChartData;

  // Opções de anos (últimos 3 anos)
  const yearOptions = Array.from({ length: 3 }, (_, i) => {
    const year = currentYear - i;
    return { value: year, label: year.toString() };
  });

  if (isLoading) {
    return <Skeletons.Chart />;
  }

  return (
    <ChartContainer>
      <TotalsContainer>
        <TotalCard>
          <TotalValue>{totals.enrichment.toLocaleString('pt-BR')}</TotalValue>
          <TotalLabel>
            Enriquecimento {customer?.customerType || 'PF'}
          </TotalLabel>
        </TotalCard>
        <TotalCard>
          <TotalValue>{totals.leads.toLocaleString('pt-BR')}</TotalValue>
          <TotalLabel>Leads {customer?.customerType || 'PF'}</TotalLabel>
        </TotalCard>
      </TotalsContainer>

      <SelectorContainer>
        <PeriodSelector>
          <PeriodButton
            active={selectedPeriod === 'monthly'}
            onClick={() => setSelectedPeriod('monthly')}
          >
            Mensal
          </PeriodButton>
          <PeriodButton
            active={selectedPeriod === 'yearly'}
            onClick={() => setSelectedPeriod('yearly')}
          >
            Anual
          </PeriodButton>
        </PeriodSelector>

        <MonthYearSelector>
          {selectedPeriod === 'monthly' && (
            <select
              value={selectedMonth}
              onChange={e => setSelectedMonth(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                marginRight: '8px',
              }}
            >
              <option value="janeiro">Janeiro</option>
              <option value="fevereiro">Fevereiro</option>
              <option value="março">Março</option>
              <option value="abril">Abril</option>
              <option value="maio">Maio</option>
              <option value="junho">Junho</option>
              <option value="julho">Julho</option>
              <option value="agosto">Agosto</option>
              <option value="setembro">Setembro</option>
              <option value="outubro">Outubro</option>
              <option value="novembro">Novembro</option>
              <option value="dezembro">Dezembro</option>
            </select>
          )}
          <select
            value={selectedYear}
            onChange={e => setSelectedYear(Number(e.target.value))}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #ddd',
            }}
          >
            {yearOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </MonthYearSelector>
      </SelectorContainer>

      <ChartWrapper>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart
            data={chartData}
            margin={{
              top: 20,
              right: 20,
              left: 10,
              bottom: 5,
            }}
          >
            <XAxis
              dataKey="period"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#666' }}
            />
            <YAxis hide />
            <Tooltip
              formatter={(value: any, name: string) => [
                value.toLocaleString('pt-BR'),
                name === 'total' ? 'Consumo Total' : name,
              ]}
              labelFormatter={label =>
                selectedPeriod === 'monthly'
                  ? `Dia ${label}`
                  : `Mês de ${label}`
              }
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }}
            />
            <Bar
              dataKey="total"
              fill="#8A1EFF"
              radius={[4, 4, 0, 0]}
              maxBarSize={40}
            >
              <LabelList dataKey="total" content={renderCustomizedLabel} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartWrapper>
    </ChartContainer>
  );
};

export default DailyConsumptionChart;
